import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Chip,
} from '@mui/material';
import api from '../api';
import config from '../config';

const AdminPanel = () => {
    const [users, setUsers] = useState([]);
    const [links, setLinks] = useState([]);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openEditRoleDialog, setOpenEditRoleDialog] = useState(false);
    const [openChangePasswordDialog, setOpenChangePasswordDialog] = useState(false);
    const [openEditOUDialog, setOpenEditOUDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false); // Новое состояние для диалога удаления
    const [userToDelete, setUserToDelete] = useState(null); // Пользователь для удаления
    const [selectedUser, setSelectedUser] = useState(null);
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        role: 'user',
        allowed_ou_ids: []
    });
    const [newPassword, setNewPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchUsers();
        fetchLinks();
    }, []);

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`${config.apiBaseUrl}/admin/users`, {
                headers: { 'x-access-token': token },
            });

            const usersWithParsedOU = response.data.map(user => {
                let ouIds = [];
                try {
                    ouIds = user.allowed_ou_ids ? JSON.parse(user.allowed_ou_ids) : [];
                } catch (e) {
                    console.error('Error parsing allowed_ou_ids for user', user.id, e);
                    ouIds = [];
                }

                if (!Array.isArray(ouIds)) {
                    ouIds = [];
                }

                return {
                    ...user,
                    allowed_ou_ids: ouIds
                };
            });

            setUsers(usersWithParsedOU);
        } catch (err) {
            console.error('Ошибка при загрузке пользователей:', err);
            setError('Ошибка при загрузке пользователей');
        }
    };

    const fetchLinks = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`${config.apiBaseUrl}/admin/ou-links`, {
                headers: { 'x-access-token': token },
            });
            setLinks(response.data);
        } catch (err) {
            console.error('Ошибка при загрузке списка ОУ:', err);
            setError('Ошибка при загрузке списка ОУ');
        }
    };

    const safeGetOUNumbers = (ouIds) => {
        try {
            if (!ouIds || !Array.isArray(ouIds)) return 'Все';
            if (ouIds.length === 0) return 'Все';

            return ouIds.map(id => {
                const ou = links.find(link => link.id === id);
                return ou ? ou.number : `ID:${id}`;
            }).join(', ');
        } catch (e) {
            console.error('Error in safeGetOUNumbers:', e);
            return 'Ошибка';
        }
    };

    const handleCreateUser = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.post(`${config.apiBaseUrl}/admin/users`, {
                ...newUser,
                allowed_ou_ids: JSON.stringify(newUser.allowed_ou_ids || [])
            }, {
                headers: { 'x-access-token': token },
            });
            setSuccess('Пользователь успешно создан');
            setError('');
            setOpenCreateDialog(false);
            setNewUser({ username: '', password: '', role: 'user', allowed_ou_ids: [] });
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка при создании пользователя');
            console.error('Ошибка при создании пользователя:', err);
        }
    };

    const handleUpdateRole = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.put(
                `${config.apiBaseUrl}/admin/users/${selectedUser.id}/role`,
                { role: selectedUser.role },
                { headers: { 'x-access-token': token } }
            );
            setSuccess('Роль пользователя обновлена');
            setError('');
            setOpenEditRoleDialog(false);
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка при обновлении роли');
            console.error('Ошибка при обновлении роли:', err);
        }
    };

    const handleUpdateAllowedOU = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.put(
                `${config.apiBaseUrl}/admin/users/${selectedUser.id}/allowed-ou`,
                { allowed_ou_ids: JSON.stringify(selectedUser.allowed_ou_ids || []) },
                { headers: { 'x-access-token': token } }
            );
            setSuccess('Доступные ОУ успешно обновлены');
            setError('');
            setOpenEditOUDialog(false);
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка при обновлении доступных ОУ');
            console.error('Ошибка при обновлении доступных ОУ:', err);
        }
    };

    const handleChangePassword = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.put(
                `${config.apiBaseUrl}/admin/users/${selectedUser.id}/password`,
                { newPassword },
                { headers: { 'x-access-token': token } }
            );
            setSuccess('Пароль успешно изменен');
            setError('');
            setOpenChangePasswordDialog(false);
            setNewPassword('');
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка при изменении пароля');
            console.error('Ошибка при изменении пароля:', err);
        }
    };

    const confirmDelete = (user) => {
        setUserToDelete(user);
        setOpenDeleteDialog(true);
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.delete(`${config.apiBaseUrl}/admin/users/${userToDelete.id}`, {
                headers: { 'x-access-token': token },
            });
            setSuccess('Пользователь успешно удален');
            setError('');
            setOpenDeleteDialog(false);
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка при удалении пользователя');
            console.error('Ошибка при удалении пользователя:', err);
        }
    };

    return (
        <Container maxWidth="lg">
            <Box mt={5} textAlign="center">
                <Typography variant="h4" gutterBottom>
                    Панель администратора
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenCreateDialog(true)}
                    sx={{ mb: 3 }}
                >
                    Создать пользователя
                </Button>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Имя пользователя</TableCell>
                                <TableCell>Роль</TableCell>
                                <TableCell>Доступные ОУ</TableCell>
                                <TableCell>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.id}</TableCell>
                                    <TableCell>{user.username}</TableCell>
                                    <TableCell>{user.role}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={safeGetOUNumbers(user.allowed_ou_ids)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="small"
                                            onClick={() => {
                                                setSelectedUser(user);
                                                setOpenEditRoleDialog(true);
                                            }}
                                        >
                                            Роль
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => {
                                                setSelectedUser(user);
                                                setOpenChangePasswordDialog(true);
                                            }}
                                        >
                                            Пароль
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => {
                                                setSelectedUser({
                                                    ...user,
                                                    allowed_ou_ids: Array.isArray(user.allowed_ou_ids)
                                                        ? [...user.allowed_ou_ids]
                                                        : []
                                                });
                                                setOpenEditOUDialog(true);
                                            }}
                                        >
                                            ОУ
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            onClick={() => confirmDelete(user)}
                                        >
                                            Удалить
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Диалог создания пользователя */}
            <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Создать нового пользователя</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Имя пользователя"
                        value={newUser.username}
                        onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Пароль"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    />
                    <Select
                        fullWidth
                        margin="normal"
                        value={newUser.role}
                        onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    >
                        <MenuItem value="user">Пользователь</MenuItem>
                        <MenuItem value="admin">Администратор</MenuItem>
                    </Select>
                    <Select
                        fullWidth
                        margin="normal"
                        multiple
                        value={newUser.allowed_ou_ids}
                        onChange={(e) => setNewUser({...newUser, allowed_ou_ids: e.target.value})}
                        renderValue={(selected) => safeGetOUNumbers(selected)}
                    >
                        {links.map((link) => (
                            <MenuItem key={link.id} value={link.id}>
                                {link.number} - {link.type}
                            </MenuItem>
                        ))}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreateDialog(false)}>Отмена</Button>
                    <Button
                        onClick={handleCreateUser}
                        color="primary"
                        disabled={!newUser.username || !newUser.password}
                    >
                        Создать
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог изменения роли */}
            <Dialog open={openEditRoleDialog} onClose={() => setOpenEditRoleDialog(false)}>
                <DialogTitle>Изменить роль пользователя</DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        value={selectedUser?.role || 'user'}
                        onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                        sx={{ mt: 2 }}
                    >
                        <MenuItem value="user">Пользователь</MenuItem>
                        <MenuItem value="admin">Администратор</MenuItem>
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditRoleDialog(false)}>Отмена</Button>
                    <Button onClick={handleUpdateRole} color="primary">Сохранить</Button>
                </DialogActions>
            </Dialog>

            {/* Диалог изменения доступных ОУ */}
            <Dialog open={openEditOUDialog} onClose={() => setOpenEditOUDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Редактировать доступные ОУ</DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        multiple
                        value={selectedUser?.allowed_ou_ids || []}
                        onChange={(e) => setSelectedUser({...selectedUser, allowed_ou_ids: e.target.value})}
                        sx={{ mt: 2 }}
                        renderValue={(selected) => safeGetOUNumbers(selected)}
                    >
                        {links.map((link) => (
                            <MenuItem key={link.id} value={link.id}>
                                {link.number} - {link.type}
                            </MenuItem>
                        ))}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditOUDialog(false)}>Отмена</Button>
                    <Button onClick={handleUpdateAllowedOU} color="primary">Сохранить</Button>
                </DialogActions>
            </Dialog>

            {/* Диалог смены пароля */}
            <Dialog open={openChangePasswordDialog} onClose={() => setOpenChangePasswordDialog(false)}>
                <DialogTitle>Смена пароля для {selectedUser?.username}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Новый пароль"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenChangePasswordDialog(false)}>Отмена</Button>
                    <Button
                        onClick={handleChangePassword}
                        color="primary"
                        disabled={!newPassword}
                    >
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог подтверждения удаления */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Подтверждение удаления</DialogTitle>
                <DialogContent>
                    Вы уверены, что хотите удалить пользователя <strong>{userToDelete?.username}</strong>?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Отмена</Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                    >
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminPanel;